import {
  PlainDate,
  type PlainYearMonth,
  genPlainDateList,
  genPlainYearMonthList,
  getEndOfMonth,
} from "@sa-nu/shared-utils/date";

export type MonthGridCell =
  | { type: "blank" }
  | { type: "date"; date: PlainDate };

export type MonthGrid = {
  yearMonth: PlainYearMonth;
  cells: MonthGridCell[];
};

/**
 * minDate〜maxDate を月ごとのカレンダーグリッド (日曜始まり・7 列) に変換する。
 *
 * 各月のセルは「月初の曜日オフセット分の blank セル + 月初〜月末の date セル」で構成し、
 * 7 個ずつ並べるとカレンダーになる (末尾の blank は入れない)。
 *
 * 端の月は週単位でトリムする:
 * 最初の月は minDate を含む週の日曜から描画し、それより前の週は省略する
 * (minDate の週が前月にまたがる場合は月初から)。
 * 同様に最後の月は maxDate を含む週の土曜まで描画し、それより後の週は省略する
 * (maxDate の週が翌月にまたがる場合は月末まで)。
 *
 * 選択可否は扱わない。minDate より前・maxDate より後の日付もセルとしては返し、
 * disabled 判定は呼び出し側 (deriveDateCellState への baseDisabled) の責務。
 */
export function buildMonthGrids(
  minDate: PlainDate,
  maxDate: PlainDate,
): MonthGrid[] {
  const yearMonths = genPlainYearMonthList(minDate, maxDate);

  // minDate が月の途中の場合、最初の月は minDate を含む週 (日曜始まり) から描画する
  const minDateWeekStart = minDate.subtract({
    days: minDate.dayOfWeek === 7 ? 0 : minDate.dayOfWeek,
  });

  // maxDate が月の途中の場合、最後の月は maxDate を含む週の土曜まで描画する
  // (ISO dayOfWeek: Mon=1 ... Sun=7、日曜始まりグリッドなので週の終わりは土曜)
  const maxDateWeekEnd = maxDate.add({
    days: maxDate.dayOfWeek === 7 ? 6 : 6 - maxDate.dayOfWeek,
  });

  return yearMonths.map((yearMonth) => {
    const firstOfMonth = minDate.with({
      year: yearMonth.year,
      month: yearMonth.month,
      day: 1,
    });
    const lastOfMonth = getEndOfMonth(firstOfMonth);

    const firstDateToRender =
      PlainDate.compare(minDateWeekStart, firstOfMonth) > 0
        ? minDateWeekStart
        : firstOfMonth;

    const lastDateToRender =
      PlainDate.compare(maxDateWeekEnd, lastOfMonth) < 0
        ? maxDateWeekEnd
        : lastOfMonth;

    // ISO dayOfWeek: Mon=1 ... Sun=7 → Sunday-first grid offset
    const blankCount =
      firstDateToRender.dayOfWeek === 7 ? 0 : firstDateToRender.dayOfWeek;

    const blanks: MonthGridCell[] = Array.from({ length: blankCount }, () => ({
      type: "blank" as const,
    }));

    const dateCells: MonthGridCell[] = genPlainDateList(
      firstDateToRender,
      lastDateToRender,
    ).map((date) => ({
      type: "date" as const,
      date,
    }));

    return { yearMonth, cells: [...blanks, ...dateCells] };
  });
}
